/*
 * delivery-estimate.js - <delivery-estimate> rows on the PDP and in the cart footer (cart page and drawer).
 *
 * Loaded (deferred) by layout/theme.liquid when either estimated delivery setting is on. The markup is
 * snippets/delivery-estimate.liquid, which has already picked the business days for the visitor's country
 * from the delivery_estimate metaobject (Website Locale List days, plus any extra days for busy periods). This
 * file only turns that window into dates in the visitor's own calendar, so cached pages never show yesterday's
 * dates. A business day is any day that is not a non-working weekday (default Saturday and Sunday) and not a
 * closed date.
 */
(() => {
  "use strict";

  if (customElements.get("delivery-estimate")) return;

  const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const DEFAULT_OFF_DAYS = [6, 0];
  const MAX_SCAN_DAYS = 400;

  const pad = (n) => String(n).padStart(2, "0");
  const isoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  // Weekday names from the metaobject list field; an empty or unusable list means Saturday and Sunday.
  const parseOffDays = (value) => {
    const days = String(value || "")
      .split(",")
      .map((name) => DAY_NAMES.indexOf(name.trim().toLowerCase()))
      .filter((day, index, all) => day >= 0 && all.indexOf(day) === index);
    return days.length && days.length < 7 ? days : DEFAULT_OFF_DAYS;
  };

  // The date `count` business days after `start`. With 0, today if it is a business day, else the next one.
  const addBusinessDays = (start, count, isOpen) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let left = Math.max(0, count);
    for (let scanned = 0; scanned < MAX_SCAN_DAYS; scanned++) {
      if (left === 0 && isOpen(date)) return date;
      date.setDate(date.getDate() + 1);
      if (left > 0 && isOpen(date)) left--;
    }
    return null;
  };

  // Always the Gregorian calendar: Safari formats en-SA and ar-SA in the Hijri calendar by default.
  // Arabic digits follow the theme setting "Numbers and currencies on Arabic pages" (html[data-hb-numerals]),
  // so the dates match the prices.
  const formatter = (locale) => {
    const options = { weekday: "short", day: "numeric", month: "short", calendar: "gregory" };
    const digits = document.documentElement.getAttribute("data-hb-numerals") === "arab" ? "arab" : "latn";
    const tag = /^ar\b/i.test(locale) ? `${locale}-u-nu-${digits}` : locale;
    try {
      return new Intl.DateTimeFormat(tag, options);
    } catch {
      return new Intl.DateTimeFormat(undefined, options);
    }
  };

  const formatWindow = (locale, from, to) => {
    const format = formatter(locale);
    if (from.getTime() === to.getTime()) return format.format(from);
    if (typeof format.formatRange === "function") return format.formatRange(from, to);
    return `${format.format(from)} – ${format.format(to)}`;
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const target = this.querySelector("[data-delivery-estimate-dates]");
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      if (!target || !Number.isFinite(min) || !Number.isFinite(max)) return;

      const offDays = parseOffDays(this.dataset.offDays);
      const closed = new Set(
        String(this.dataset.closed || "")
          .split(",")
          .map((day) => day.trim())
          .filter(Boolean)
      );
      const isOpen = (date) => !offDays.includes(date.getDay()) && !closed.has(isoDate(date));

      const today = new Date();
      const from = addBusinessDays(today, min, isOpen);
      const to = addBusinessDays(today, Math.max(min, max), isOpen);
      if (!from || !to) return;

      target.textContent = formatWindow(this.dataset.locale || document.documentElement.lang, from, to);
    }
  }

  customElements.define("delivery-estimate", DeliveryEstimate);
})();
